import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * MapStandard Component
 * Initializes and manages a Mapbox map with markers and popups
 * 
 * @constructor
 * @param {Object} options - Configuration options
 * @param {jQuery} options.$el - jQuery element containing the component
 * @param {string} options.baseClass - Base CSS class name
 * @param {string} options.accessToken - Mapbox access token
 * @param {string} options.style - Mapbox style URL
 * @param {Array<number>} options.center - Initial map center [lng, lat]
 * @param {number} options.zoom - Initial zoom level
 */
var MapStandard = function(options) {

	this.options = {
		$el: false,
		baseClass: 'mol-MapStandard',
		accessToken: 'pk.eyJ1IjoiYXV0aGVudGljZmYiLCJhIjoiUmtjWTY2byJ9.QeHZuWdaMRJlobq_RgXngw',
		style: 'mapbox://styles/authenticff/cmfzw19aj005e01s82akoh8mi',
		center: [-77.037855, 38.891725],
		zoom: 15.19
	};

	$.extend(true, this.options, options);

	this.map = null;
	this.$mapContainer = null;
	this.mapItems = [];
	this.markers = [];

};

/**
 * Initialize the map component
 * Sets up the Mapbox map, loads map items, and handles geocoding
 */
MapStandard.prototype.init = function() {

	var self = this;

	// Find the map container
	this.$mapContainer = this.options.$el.find('.map-standard_component');

	if (!this.$mapContainer.length) {
		console.warn('MapStandard: Map container not found');
		return;
	}

	// Get map items from data attribute
	var mapItemsData = this.options.$el.attr('data-map-items');
	if (mapItemsData) {
		try {
			this.mapItems = JSON.parse(mapItemsData);
		} catch (e) {
			console.warn('MapStandard: Failed to parse map items data', e);
			this.mapItems = [];
		}
	} else {
		this.mapItems = [];
	}

	// Set access token
	mapboxgl.accessToken = this.options.accessToken;

	// Calculate initial center and zoom from items with coordinates
	var initialCenter = this.options.center;
	var initialZoom = this.options.zoom;
	
	if (this.mapItems && this.mapItems.length > 0) {
		// Get all items that already have coordinates
		var itemsWithCoords = this.mapItems.filter(function(item) {
			return item.coordinates && item.coordinates.length === 2;
		});
		
		if (itemsWithCoords.length > 0) {
			// Calculate bounds from existing coordinates
			var bounds = new mapboxgl.LngLatBounds();
			itemsWithCoords.forEach(function(item) {
				bounds.extend(item.coordinates);
			});
			
			// Get center from bounds
			var center = bounds.getCenter();
			initialCenter = [center.lng, center.lat];
			
			// Set a wider zoom initially (will be refined after all markers are added)
			initialZoom = 12;
		}
	}

	// Initialize the map (will fit bounds after all markers are added)
	this.map = new mapboxgl.Map({
		container: this.$mapContainer[0],
		style: this.options.style,
		center: initialCenter,
		zoom: initialZoom
	});

	// Handle style errors (e.g., missing layers like hillshade)
	this.map.on('error', function(e) {
		if (e.error && e.error.message) {
			if (e.error.message.indexOf('hillshade') !== -1 || 
				e.error.message.indexOf('does not exist on source') !== -1) {
				console.warn('MapStandard: Style layer error detected:', e.error.message);
				// Try to remove problematic layer after style loads
				self.map.once('style.load', function() {
					self.removeProblematicLayer('hillshade');
				});
			} else {
				console.error('MapStandard: Map error', e.error);
			}
		}
	});

	// Handle style loading - remove problematic layers proactively
	this.map.on('style.load', function() {
		self.removeProblematicLayer('hillshade');
	});

	// Add navigation controls (optional)
	this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');

	// Wait for map to load
	this.map.on('load', function() {
		if (self.mapItems && self.mapItems.length > 0) {
			self.geocodeAddressesAndAddMarkers();
		}
	});

};

/**
 * Remove a problematic layer from the map style
 * Used to handle style errors where a layer references a missing source
 * 
 * @param {string} layerId - The ID of the layer to remove
 */
MapStandard.prototype.removeProblematicLayer = function(layerId) {

	try {
		var layer = null;
		try {
			layer = this.map.getLayer(layerId);
		} catch (err) {
			// Layer doesn't exist, which is fine
			return;
		}
		
		if (layer) {
			try {
				// Check if the source exists
				var sourceId = layer.source;
				if (sourceId) {
					try {
						var source = this.map.getSource(sourceId);
						if (!source) {
							// Source doesn't exist, remove the layer
							this.map.removeLayer(layerId);
						}
					} catch (sourceErr) {
						// Source doesn't exist, remove the layer
						this.map.removeLayer(layerId);
					}
				}
			} catch (err) {
				// If we can't check source, try to remove layer anyway
				try {
					this.map.removeLayer(layerId);
				} catch (removeErr) {
					// Layer might not be removable
					console.warn('MapStandard: Could not remove layer', layerId, removeErr);
				}
			}
		}
	} catch (err) {
		// Error is non-fatal, map will still work
		console.warn('MapStandard: Error checking style layers', err);
	}

};

/**
 * Geocode addresses and add markers to the map
 * Handles both items with existing coordinates and items that need geocoding
 * Fits map bounds after all markers are added
 */
MapStandard.prototype.geocodeAddressesAndAddMarkers = function() {

	var self = this;
	
	if (!this.mapItems || this.mapItems.length === 0) {
		return;
	}

	var geocodePromises = [];
	var itemsToGeocode = [];

	// Process items: add markers with coordinates, queue others for geocoding
	this.mapItems.forEach(function(item, index) {
		if (!item.address && !item.coordinates) {
			console.warn('MapStandard: Item missing address and coordinates', item);
			return;
		}

		// If coordinates already exist, add marker directly
		if (item.coordinates && item.coordinates.length === 2) {
			self.addMarker(item.coordinates, item);
		} else if (item.address) {
			// Need to geocode
			itemsToGeocode.push({ item: item, index: index });
		}
	});

	// If all items already have coordinates, fit bounds after a brief delay
	if (itemsToGeocode.length === 0) {
		if (this.markers.length > 0) {
			setTimeout(function() {
				self.fitMapToMarkers();
			}, 100);
		}
		return;
	}

	// Geocode each address that needs it
	itemsToGeocode.forEach(function(itemData) {
		var item = itemData.item;
		var index = itemData.index;

		var geocodeUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + 
			encodeURIComponent(item.address) + 
			'.json?access_token=' + self.options.accessToken;

		var promise = $.ajax({
			url: geocodeUrl,
			method: 'GET'
		}).done(function(response) {
			if (response.features && response.features.length > 0) {
				var coordinates = response.features[0].center; // [lng, lat]
				
				// Store coordinates in the item
				self.mapItems[index].coordinates = coordinates;
				
				// Add marker to map
				self.addMarker(coordinates, item);
			} else {
				console.warn('MapStandard: No results for address', item.address);
			}
		}).fail(function(xhr, status, error) {
			console.error('MapStandard: Geocoding error for', item.address, error);
		});

		geocodePromises.push(promise);
	});

	// Wait for all geocoding to complete, then fit bounds
	$.when.apply($, geocodePromises).then(function() {
		if (self.markers.length > 0) {
			setTimeout(function() {
				self.fitMapToMarkers();
			}, 100);
		} else {
			console.warn('MapStandard: No markers were successfully added');
		}
	});

};

/**
 * Add a marker to the map with a popup
 * 
 * @param {Array<number>} coordinates - Map coordinates [lng, lat]
 * @param {Object} item - The map item data containing popup information
 * @param {string} item.popupHtml - Pre-rendered HTML for the popup (optional)
 * @param {string} item.heading - Popup heading text (fallback)
 * @param {string} item.address - Popup address text (fallback)
 * @param {Object} item.button - Button link object (fallback)
 */
MapStandard.prototype.addMarker = function(coordinates, item) {

	// Create marker element
	var el = document.createElement('div');
	el.className = 'map-standard_marker';
	el.style.width = '30px';
	el.style.height = '30px';
	el.style.borderRadius = '50%';
	el.style.backgroundColor = '#000';
	el.style.cursor = 'pointer';

	// Create and add marker to map
	var marker = new mapboxgl.Marker(el)
		.setLngLat(coordinates)
		.addTo(this.map);

	// Use pre-rendered popup HTML if available, otherwise create simple HTML
	var popupContent = item.popupHtml || '';
	
	if (!popupContent) {
		popupContent = '<div class="map-standard_popup">';
		if (item.heading) {
			popupContent += '<h3>' + item.heading + '</h3>';
		}
		if (item.address) {
			popupContent += '<p>' + item.address + '</p>';
		}
		if (item.button && item.button.url) {
			popupContent += '<a href="' + item.button.url + '" target="' + (item.button.target || '_self') + '">' + 
				(item.button.text || 'View Website') + '</a>';
		}
		popupContent += '</div>';
	}

	// Create and attach popup
	var popup = new mapboxgl.Popup({ offset: 25 })
		.setHTML(popupContent);

	marker.setPopup(popup);

	// Store marker reference
	this.markers.push(marker);

};

/**
 * Fit map bounds to show all markers
 * Centers the map view to include all markers with appropriate padding
 * Handles single marker case by centering with a fixed zoom level
 */
MapStandard.prototype.fitMapToMarkers = function() {

	if (this.markers.length === 0) {
		console.warn('MapStandard: No markers to fit bounds to');
		return;
	}

	// Create bounds object
	var bounds = new mapboxgl.LngLatBounds();

	// Extend bounds to include each marker
	this.markers.forEach(function(marker) {
		bounds.extend(marker.getLngLat());
	});

	// If only one marker, center on it with a reasonable zoom level
	if (this.markers.length === 1) {
		var singleMarker = this.markers[0];
		this.map.setCenter(singleMarker.getLngLat());
		this.map.setZoom(14);
		return;
	}

	// Fit map to bounds with padding
	this.map.fitBounds(bounds, {
		padding: {
			top: 50,
			bottom: 50,
			left: 50,
			right: 50
		},
		maxZoom: 15,
		duration: 1000
	});

};

export default MapStandard;
